import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BadgeTone } from '../common/Badge';
import { StatusChip } from '../common/StatusChip';
import {
  IconSpinner,
  IconStatusNew,
  IconWarning,
  IconHourglass,
  IconSuccess,
  IconVoid,
} from '../../icons';
import type { UnifiedStatus } from '../../utils/unifiedInbox/types';

/**
 * `inbox/UnifiedStatusBadge` — the per-row kanalunabhängige Status-Badge
 * (§6.2/§6.5): `processing`/`new`/`failed`/`draft`/`booked`/`discarded`.
 *
 * plan-ux-status-chips-storno-inbox.md §B: internally rebuilt on top of
 * {@link StatusChip} (icon-only, 1:1 pattern with `EmailStatusChip`/
 * `CancellationStatusChip`) — component name, filename, props (`{ status }`)
 * and the single consumer (`UnifiedInboxTable`) stay unchanged. The visible
 * chip shows ONLY the symbol icon; the status word
 * (`posteingang:unifiedInbox.status.<status>`) is exposed as a
 * screenreader-only `srLabel` and additionally shown as a bold first line
 * inside the popover, with the detail sentence
 * (`posteingang:unifiedInbox.statusDetail.<status>`) underneath.
 *
 * `STATUS_TONE` map (unverändert, Bestand): processing=info, new=neutral,
 * failed=warning (bewusst, nicht danger — behebbar/erneut versuchbar),
 * draft=caution, booked=success, discarded=neutral.
 *
 * `processing` rotates ONLY via `motion-safe:animate-spin` (reduced-motion
 * respected).
 *
 * @module components/inbox/UnifiedStatusBadge
 */
export interface UnifiedStatusBadgeProps {
  status: UnifiedStatus;
}

const STATUS_TONE: Record<UnifiedStatus, BadgeTone> = {
  processing: 'info',
  new: 'neutral',
  failed: 'warning',
  draft: 'caution',
  booked: 'success',
  discarded: 'neutral',
};

/** Symbolsprache (§B). Icons sind in StatusChip `aria-hidden` gewrappt. */
const ICON_BY_STATUS: Record<UnifiedStatus, ReactNode> = {
  processing: (
    <span data-testid="inbox-status-icon-processing">
      <IconSpinner size={14} className="motion-safe:animate-spin" />
    </span>
  ),
  new: (
    <span data-testid="inbox-status-icon-new">
      <IconStatusNew size={14} />
    </span>
  ),
  failed: (
    <span data-testid="inbox-status-icon-failed">
      <IconWarning size={14} />
    </span>
  ),
  draft: (
    <span data-testid="inbox-status-icon-draft">
      <IconHourglass size={14} />
    </span>
  ),
  booked: (
    <span data-testid="inbox-status-icon-booked">
      <IconSuccess size={14} />
    </span>
  ),
  discarded: (
    <span data-testid="inbox-status-icon-discarded">
      <IconVoid size={14} />
    </span>
  ),
};

export const UnifiedStatusBadge: React.FC<UnifiedStatusBadgeProps> = ({ status }) => {
  const { t } = useTranslation('posteingang');

  // Fail-safe gegen unerwartete Werte: unbekannter Status ⇒ neutraler Chip
  // ohne Icon, statt eine `undefined`-Tone-Klasse zu erzeugen (Muster
  // EmailStatusChip).
  const tone = STATUS_TONE[status] ?? 'neutral';
  const icon = ICON_BY_STATUS[status];

  const shortLabel = t(`unifiedInbox.status.${status}`);
  const sentence = t(`unifiedInbox.statusDetail.${status}`);

  const detail = (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-primary">{shortLabel}</span>
      <span>{sentence}</span>
    </div>
  );

  return (
    <StatusChip
      tone={tone}
      icon={icon}
      label={null}
      srLabel={shortLabel}
      detailTitle={t('unifiedInbox.statusDetail.title')}
      detail={detail}
      data-testid={`unified-status-chip-${status}`}
    />
  );
};

export default UnifiedStatusBadge;
